import React from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Spin,
  Tag,
  Table,
  Typography,
} from 'antd';
import {
  BookOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { formatDateShort, truncate } from './services';
import type { QueryRecord, DailyStat, OverviewCardConfig } from './models';
import { useDashboard } from './handlers';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Daily stats table columns
// ---------------------------------------------------------------------------

const dailyColumns: ColumnsType<DailyStat> = [
  {
    title: '日期',
    dataIndex: 'date',
    key: 'date',
    render: (v: string) => formatDateShort(v),
    width: 140,
  },
  {
    title: '查詢次數',
    dataIndex: 'queries',
    key: 'queries',
    align: 'right',
    width: 120,
    render: (v: number | undefined | null) => (v ?? 0).toLocaleString(),
  },
  {
    title: 'Tokens 使用',
    dataIndex: 'tokens',
    key: 'tokens',
    align: 'right',
    width: 140,
    render: (v: number | undefined | null) => (v ?? 0).toLocaleString(),
  },
  {
    title: '成本 (USD)',
    dataIndex: 'cost',
    key: 'cost',
    align: 'right',
    width: 140,
    render: (v: number | undefined | null) => `$${(v ?? 0).toFixed(4)}`,
  },
];

// ---------------------------------------------------------------------------
// Recent queries table columns
// ---------------------------------------------------------------------------

const queryColumns: ColumnsType<QueryRecord> = [
  {
    title: '查詢內容',
    dataIndex: 'query',
    key: 'query',
    ellipsis: true,
    render: (v: string) => (
      <Text title={v}>{truncate(v, 50)}</Text>
    ),
  },
  {
    title: '模型',
    dataIndex: 'model_used',
    key: 'model_used',
    width: 160,
    render: (v: string) => (
      <Tag color="blue">{v || '-'}</Tag>
    ),
  },
  {
    title: 'Tokens',
    dataIndex: 'total_tokens',
    key: 'total_tokens',
    align: 'right',
    width: 110,
    render: (v: number | undefined | null) => (v ?? 0).toLocaleString(),
  },
  {
    title: '成本 (USD)',
    dataIndex: 'estimated_cost',
    key: 'estimated_cost',
    align: 'right',
    width: 130,
    render: (v: number | undefined | null) => `$${(v ?? 0).toFixed(7)}`,
  },
  {
    title: '時間',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 130,
    render: (v: string) => formatDateShort(v),
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MekakDashboard: React.FC = () => {
  const { loading, stats } = useDashboard();

  const k = stats?.knowledge;
  const s = stats?.system;
  const overviewCards: OverviewCardConfig[] = stats
    ? [
        {
          title: '知識條目總數',
          value: k?.total_count ?? 0,
          icon: <BookOutlined />,
          color: '#1677ff',
        },
        {
          title: '總瀏覽次數',
          value: k?.total_views ?? 0,
          icon: <EyeOutlined />,
          color: '#52c41a',
        },
        {
          title: '近 7 天新增',
          value: k?.recent_count ?? 0,
          icon: <PlusCircleOutlined />,
          color: '#faad14',
        },
        {
          title: 'AI 查詢總次數',
          value: s?.total_queries ?? 0,
          icon: <RobotOutlined />,
          color: '#722ed1',
        },
        {
          title: '總 Tokens 使用',
          value: s?.total_tokens ?? 0,
          icon: <ThunderboltOutlined />,
          color: '#13c2c2',
        },
        {
          title: '總成本',
          value: `$${(s?.total_cost ?? 0).toFixed(7)}`,
          icon: <DollarCircleOutlined />,
          color: '#f5222d',
          suffix: 'USD',
        },
      ]
    : [];

  const dailyStats: DailyStat[] = stats?.system?.daily_stats ?? [];
  const queryRecords: QueryRecord[] = stats?.system?.query_records ?? [];
  const topTags: Array<{ tag: string; count: number }> =
    stats?.knowledge?.top_tags ?? [];

  return (
    <div>
      <Spin spinning={loading} size="large" tip="載入統計資料中...">
        {/* Overview cards */}
        <Row gutter={[16, 16]} className="mb-6">
          {overviewCards.map((card) => (
            <Col key={card.title} xs={24} sm={12} md={8} lg={4}>
              <Card bordered={false} className="h-full shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <span
                    style={{
                      fontSize: 22,
                      color: card.color,
                      lineHeight: 1,
                    }}
                  >
                    {card.icon}
                  </span>
                  <Text type="secondary" className="text-sm">
                    {card.title}
                  </Text>
                </div>
                <Statistic
                  value={card.value}
                  valueStyle={{ fontSize: 22, fontWeight: 600, color: card.color }}
                  suffix={card.suffix}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Top tags */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col span={24}>
            <Card
              title="熱門標籤"
              bordered={false}
              className="shadow-sm"
            >
              {topTags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {topTags.map(({ tag, count }) => (
                    <Tag
                      key={tag}
                      color="blue"
                      style={{ fontSize: 13, padding: '2px 10px' }}
                    >
                      {tag}
                      <span className="ml-1 text-blue-300 text-xs">
                        ({count})
                      </span>
                    </Tag>
                  ))}
                </div>
              ) : (
                <Text type="secondary">尚無標籤資料</Text>
              )}
            </Card>
          </Col>
        </Row>

        {/* Daily usage table */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col span={24}>
            <Card
              title="每日使用統計"
              bordered={false}
              className="shadow-sm"
            >
              <Table<DailyStat>
                columns={dailyColumns}
                dataSource={dailyStats}
                rowKey="date"
                pagination={{
                  pageSize: 7,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 天`,
                }}
                size="small"
                locale={{ emptyText: '尚無每日統計資料' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Recent query records */}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card
              title="近期查詢紀錄"
              bordered={false}
              className="shadow-sm"
            >
              <Table<QueryRecord>
                columns={queryColumns}
                dataSource={queryRecords}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 筆`,
                }}
                size="small"
                locale={{ emptyText: '尚無查詢紀錄' }}
                scroll={{ x: 700 }}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default MekakDashboard;

